import { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Plus, Edit2, Trash2, DollarSign, Target, Save, MoreVertical, ChevronRight, Users
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const Pipeline = ({ deals, contacts, onRefresh }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);
  const [formData, setFormData] = useState({
    title: "", value: "", stage: "lead", contact_id: "none", probability: 20, notes: ""
  });

  const stages = [
    { id: "lead", label: "Lead", color: "purple" },
    { id: "contacted", label: "Contacted", color: "orange" },
    { id: "proposal", label: "Proposal", color: "blue" },
    { id: "negotiation", label: "Negotiation", color: "gold" },
    { id: "won", label: "Won", color: "green" },
    { id: "lost", label: "Lost", color: "red" },
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
  };

  const handleOpenDialog = (deal = null) => {
    if (deal) {
      setEditingDeal(deal);
      setFormData({
        title: deal.title,
        value: deal.value.toString(),
        stage: deal.stage,
        contact_id: deal.contact_id || "none",
        probability: deal.probability,
        notes: deal.notes || ""
      });
    } else {
      setEditingDeal(null);
      setFormData({ title: "", value: "", stage: "lead", contact_id: "none", probability: 20, notes: "" });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        ...formData,
        value: parseFloat(formData.value) || 0,
        probability: parseInt(formData.probability) || 0
      };
      if (!payload.contact_id || payload.contact_id === "none") delete payload.contact_id;

      if (editingDeal) {
        await axios.put(`${API}/deals/${editingDeal.id}`, payload);
      } else {
        await axios.post(`${API}/deals`, payload);
      }
      setDialogOpen(false);
      onRefresh();
    } catch (error) {
      console.error("Error saving deal:", error);
    }
  };

  const handleStageChange = async (dealId, newStage) => {
    try {
      await axios.put(`${API}/deals/${dealId}`, { stage: newStage });
      onRefresh();
    } catch (error) {
      console.error("Error updating deal stage:", error);
    }
  };

  const getContactName = (contactId) => {
    const contact = contacts.find(c => c.id === contactId);
    return contact ? contact.name : '';
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="pipeline-view">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-cabinet text-3xl font-bold text-slate-900">Pipeline</h2>
          <p className="text-slate-500 font-manrope">Track your deals through stages</p>
        </div>
        <Button 
          data-testid="add-deal-btn"
          onClick={() => handleOpenDialog()} 
          className="bg-royal-blue-700 hover:bg-royal-blue-800"
        >
          <Plus size={18} className="mr-2" /> Add Deal
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {stages.map((stage) => {
          const stageDeals = deals.filter(d => d.stage === stage.id);
          const stageValue = stageDeals.reduce((sum, d) => sum + d.value, 0);

          return (
            <div key={stage.id} className="space-y-3">
              <div className={`p-3 rounded-lg stage-${stage.id} border`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold">{stage.label}</span>
                  <Badge variant="outline">{stageDeals.length}</Badge>
                </div>
                <p className="text-sm font-medium">{formatCurrency(stageValue)}</p>
              </div>

              <ScrollArea className="h-[500px]">
                <div className="space-y-3 pr-2">
                  {stageDeals.map((deal) => (
                    <Card 
                      key={deal.id} 
                      className="border-slate-200 card-hover cursor-pointer"
                      onClick={() => handleOpenDialog(deal)}
                      data-testid={`deal-card-${deal.id}`}
                    >
                      <CardContent className="p-3">
                        <h4 className="font-medium text-slate-900 mb-2 truncate">{deal.title}</h4>
                        <p className="text-lg font-bold text-royal-blue-700 mb-2">{formatCurrency(deal.value)}</p>
                        {deal.contact_id && (
                          <p className="text-sm text-slate-500 truncate">
                            <Users size={12} className="inline mr-1" />
                            {getContactName(deal.contact_id)}
                          </p>
                        )}
                        <div className="mt-2 flex items-center gap-2">
                          <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gold-500 rounded-full"
                              style={{ width: `${deal.probability}%` }}
                            />
                          </div>
                          <span className="text-xs text-slate-500">{deal.probability}%</span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {stageDeals.length === 0 && (
                    <div className="text-center py-8 text-slate-400 text-sm">
                      No deals
                    </div>
                  )}
                </div>
              </ScrollArea>
            </div>
          );
        })}
      </div>

      {/* Deal Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-cabinet">{editingDeal ? 'Edit Deal' : 'Add Deal'}</DialogTitle>
            <DialogDescription>Fill in the deal details below</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Deal Title *</Label>
              <Input 
                id="title"
                data-testid="deal-title-input"
                value={formData.title} 
                onChange={(e) => setFormData({...formData, title: e.target.value})} 
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="value">Value ($)</Label>
                <Input 
                  id="value"
                  type="number"
                  data-testid="deal-value-input"
                  value={formData.value} 
                  onChange={(e) => setFormData({...formData, value: e.target.value})} 
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="probability">Probability (%)</Label>
                <Input 
                  id="probability"
                  type="number"
                  min="0"
                  max="100"
                  data-testid="deal-probability-input"
                  value={formData.probability} 
                  onChange={(e) => setFormData({...formData, probability: e.target.value})} 
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="stage">Stage</Label>
              <Select value={formData.stage} onValueChange={(v) => setFormData({...formData, stage: v})}>
                <SelectTrigger data-testid="deal-stage-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lead">Lead</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="proposal">Proposal</SelectItem>
                  <SelectItem value="negotiation">Negotiation</SelectItem>
                  <SelectItem value="won">Won</SelectItem>
                  <SelectItem value="lost">Lost</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact">Related Contact</Label>
              <Select value={formData.contact_id} onValueChange={(v) => setFormData({...formData, contact_id: v})}>
                <SelectTrigger data-testid="deal-contact-select">
                  <SelectValue placeholder="Select contact" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {contacts.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea 
                id="notes"
                data-testid="deal-notes-input"
                value={formData.notes} 
                onChange={(e) => setFormData({...formData, notes: e.target.value})} 
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button 
              data-testid="save-deal-btn"
              onClick={handleSubmit} 
              className="bg-royal-blue-700 hover:bg-royal-blue-800"
            >
              <Save size={16} className="mr-2" /> Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

